import ContextualBar from '@/components/ContextualBar';
import CriticalBanner from '@/components/CriticalBanner';
import CustomCard from '@/components/customCard';
import GridLayoutPreview from '@/components/GridLayoutPreview';
import Layout from '@/components/layout';
import PreviewProductLineItems from '@/components/Preview/PreviewProductLineItems';
import RegularText from '@/components/RegularText';
import Switch from '@/components/Switch';
import { stickyTop } from '@/constants';
import { PATH } from '@/constants/path';
import useToast from '@/hooks/useToast';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import { Icon, Text, TextField } from '@shopify/polaris';
import { AlertCircleIcon } from '@shopify/polaris-icons';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { EditProductLineItemsStyled } from './styled';
import useHandleContextual from '@/hooks/useHandleContextureBar';
import appbarSlice, { isShowContextualBarSelector } from '@/redux/slice/appbar.slice';
import { useCustomUserIdClarity } from '@/hooks/useCustomUserIdClarity';
import ModalConfirmLeavePage from '@/components/ModalConfirmLeavePage';
interface IProps {}

export default function EditProductLineItems(props: IProps) {
  useCustomUserIdClarity('Checkout product line items');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { show } = useToast();
  const contextualBar = useHandleContextual();
  const isShowContextualBar = useSelector(isShowContextualBarSelector);
  const isSkip = useSelector(isSkipApiSelector);
  const [update, { isLoading: isLoadingUpdate }] = apiCaller.useUpdateCheckoutAppearanceMutation();
  const { data: dataAppearance, isLoading: isLoadingAppearance } = apiCaller.useGetCheckoutAppearanceQuery(undefined, {
    skip: isSkip,
  });
  const [errorTitle, setErrorTitle] = useState('');
  const [openModalConfirmLeave, setOpenModalConfirmLeave] = useState(false);
  const [error, setError] = useState<{ status: boolean; msg: string }>({ status: false, msg: '' });

  const initialState = useMemo(() => {
    if (dataAppearance?.data)
      return {
        labelText: dataAppearance?.data?.labelText || 'Estimated between',
        isShowShippingMethodName: !!dataAppearance?.data?.isShowShippingMethodName,
        isAddETAToAdditionalDetails: !!dataAppearance?.data?.isAddETAToAdditionalDetails,
      };
    return {
      labelText: 'Estimated between',
      isShowShippingMethodName: true,
      isAddETAToAdditionalDetails: true,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(dataAppearance)]);

  const [state, setState] = useState(initialState);

  useEffect(() => {
    const temp = !isEqual(state, initialState);
    dispatch(appbarSlice.actions.handleUpdateContextualBarState({ isShowContextualBar: temp }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialState), JSON.stringify(state)]);

  const handleUpdateState = useCallback(
    (key: keyof typeof state) => (value: string | boolean) => {
      setState((prev) => ({ ...prev, [key]: value }));
      if (error.status) setError({ status: false, msg: '' });
    },
    [error.status],
  );

  const handleDiscard = () => {
    setState(initialState);
    errorTitle && setErrorTitle('');
  };

  const handleSave = async () => {
    if (isSkip) return;
    try {
      const res = await update({ ...state }).unwrap();
      if (res.code === 200) {
        show('Saved');
      }
      navigate(PATH.CHECKOUT_EXTENSION);
    } catch (error) {
      //@ts-ignore:next-line
      setError({ status: true, msg: error?.data?.message?.toString() });
    }
  };

  const handleClickBackAction = useCallback(() => {
    dispatch(appbarSlice.actions.handleUpdateContextualBarState({ isShowContextualBar: false }));
    navigate(PATH.CHECKOUT_EXTENSION);
    contextualBar?.hide();
  }, [contextualBar, dispatch, navigate]);

  return (
    <Layout
      title="Customize checkout product line items"
      backAction={{
        onAction: () => {
          isShowContextualBar ? setOpenModalConfirmLeave(true) : handleClickBackAction();
        },
      }}
      headers={
        <ContextualBar
          isVisible={isShowContextualBar}
          saveAction={handleSave}
          loadingSave={isLoadingUpdate || isLoadingAppearance}
          isDisabled={!!errorTitle}
          discardAction={handleDiscard}
        />
      }
    >
      <GridLayoutPreview>
        <EditProductLineItemsStyled className="d-flex flex-column gap-12">
          {error.status && <CriticalBanner isVisible={error.status}>{error.msg}</CriticalBanner>}

          <CustomCard title={'Label text'} required>
            <div className="d-flex align-center mt-4">
              <div style={{ width: '20px', height: '20px' }}>
                <Icon source={AlertCircleIcon} tone="subdued" />
              </div>
              <div className="ml-4">
                <Text as="span" tone="subdued">
                  If you enable “Show estimated text on cart/checkout page” in estimate rules, you need to set the same Label text
                  to prevent duplicate delivery estimates in checkout.
                </Text>
              </div>
            </div>
            <div className="mt-8">
              <TextField
                value={state.labelText}
                onChange={(value) => {
                  handleUpdateState('labelText')(value);
                  if (errorTitle) setErrorTitle('');
                }}
                maxLength={255}
                showCharacterCount
                label=""
                labelHidden
                autoComplete="off"
                error={errorTitle}
                onBlur={() => {
                  if (!state.labelText) setErrorTitle('This field is required');
                }}
              />
            </div>
          </CustomCard>

          <CustomCard
            title={'Show shipping method name'}
            titleContentRight={
              <Switch isActive={state.isShowShippingMethodName} onSwitch={handleUpdateState('isShowShippingMethodName')} />
            }
          >
            <RegularText>This will show before the message text</RegularText>
          </CustomCard>

          <CustomCard
            title={'Add ETA to “Additional details” in Shopify Order Management'}
            titleContentRight={
              <Switch isActive={state.isAddETAToAdditionalDetails} onSwitch={handleUpdateState('isAddETAToAdditionalDetails')} />
            }
          >
            <RegularText>
              The ETA shown in each product line item will also appear in the order’s “Additional details” section in Shopify.
            </RegularText>
          </CustomCard>
        </EditProductLineItemsStyled>
        <PreviewProductLineItems
          top={stickyTop}
          labelText={state.labelText}
          isShowShippingMethodName={state.isShowShippingMethodName}
        />
      </GridLayoutPreview>
      <ModalConfirmLeavePage
        open={openModalConfirmLeave}
        onClose={() => setOpenModalConfirmLeave(false)}
        onConfirm={handleClickBackAction}
      />
    </Layout>
  );
}
